// src/app/app.routes.ts
import { Routes } from '@angular/router';

import { LoginComponent } from './features/login/login.component';
import { HomeLayoutComponent } from './layouts/home-layout/home-layout.component';

import { AdminDashboardComponent } from './features/dashboards/admin-dashboard/admin-dashboard.component';
import { OpsRiskAndComplianceComponent } from './features/ops-risk-and-compliance/ops-risk-and-compliance.component';
import { UsersAndRequestsComponent } from './features/portal-user-management/users-and-requests/users-and-requests.component';
import { EmployersAndRequestsComponent } from './features/clients/employers/employers-and-requests/employers-and-requests.component';
import { CixorMobileUserManagementComponent } from './features/clients/cixor-mobile-users/cixor-mobile-user-management/cixor-mobile-user-management.component';
import { RolloverManagementComponent } from './features/credit-and-risk/rollover-management/rollover-management.component';
import { CumulativeIncomeComponent } from './features/treasury/cumulative-income/cumulative-income.component';
import { CreditLineComponent } from './features/manage-payment/cixor-payments/cixor-payment.component';
import { WageWithdrawalsComponent } from './features/treasury/wage-withdrawals/wage-withdrawals.component';

import { AuthGuard } from './core/guards/auth/auth.guard';
import { AlreadyLoggedInGuard } from './core/guards/already-logged-in/already-logged-in.guard';
import { RoutePermissionGuard } from './core/guards/route-permission/route-permission.guard';
import { WalletWithdrawalsComponent } from './features/treasury/wallet-withdrawals/wallet-withdrawals.component';
import { CreditManagementComponent } from './features/credit-and-risk/credit-management/credit-management.component';
import { ContributorManagementComponent } from './features/fundManagement/contributor-management/contributor-management.component';
import { SummaryManagementComponent } from './features/fundManagement/summary-management/summary-management.component';
import { PaymentHistoryComponent } from './features/manage-payment/payment-history/payment-history.component';
import { PayrollPaymentsComponent } from './features/manage-payment/payroll-payments/payroll-payments.component';
import { LateFeeConfigurationComponent } from './features/configuration/late-fee-configuration/late-fee-configuration.component';
import { CompanyEmployeesComponent } from './features/clients/company-employees/company-employees.component';
import { IndustryConfigurationComponent } from './features/configuration/industry-configuration/industry-configuration.component';

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
    canActivate: [AlreadyLoggedInGuard],
  },

  {
    path: '',
    component: HomeLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      { path: '', redirectTo: '/dashboard', pathMatch: 'full' },

      // Dashboard: NO permission guard (always visible if logged in)
      { path: 'dashboard', component: AdminDashboardComponent },

      // Clients
      {
        path: 'client',
        children: [
          {
            path: 'mobile-users',
            component: CixorMobileUserManagementComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'clients.mobileUsers', action: 'View' },
          },
          {
            path: 'employer',
            component: EmployersAndRequestsComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'clients.employer', action: 'View' },
          },
          {
            path: 'company-employees',
            component: CompanyEmployeesComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'clients.companyEmployees', action: 'View' },
          },
        ],
      },

      // Treasury
      {
        path: 'treasury',
        children: [
          {
            path: 'cumulative-income',
            component: CumulativeIncomeComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'treasury.cumulativeIncome', action: 'View' },
          },
          {
            path: 'wage-Withdrawals',
            component: WageWithdrawalsComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'treasury.wageWithdrawals', action: 'View' },
          },
          {
            path: 'wallet-withdrawals',
            component: WalletWithdrawalsComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'treasury.walletWithdrawals', action: 'View' },
          },
        ],
      },

      // Cixor Payment Management
      {
        path: 'manageCixorPayments',
        children: [
          {
            path: 'cixor-payments',
            component: CreditLineComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'manageCixorPayments.cixorPayments', action: 'View' },
          },
          {
            path: 'payment-history',
            component: PaymentHistoryComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'manageCixorPayments.paymentHistory', action: 'View' },
          },
          {
            path: 'payroll-payments',
            component: PayrollPaymentsComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'manageCixorPayments.payrollPayments', action: 'View' },
          },
        ],
      },

      // Fund Management
      {
        path: 'fundManagement',
        children: [
          {
            path: 'contributor-management',
            component: ContributorManagementComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'fundManagement.contributorManagement', action: 'View' },
          },
          {
            path: 'summary-management',
            component: SummaryManagementComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'fundManagement.summaryManagement', action: 'View' },
          },
        ],
      },

      // Credit & Risk
      {
        path: 'credit-and-risk',
        children: [
          {
            path: 'rollover-management',
            component: RolloverManagementComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'creditRisk.manageRollovers', action: 'View' },
          },
          {
            path: 'credit-management',
            component: CreditManagementComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'creditRisk.manageCredit', action: 'View' },
          },
        ],
      },

      // Ops Risk & Compliance
      {
        path: 'ops-risk-and-compliance',
        component: OpsRiskAndComplianceComponent,
        canActivate: [AuthGuard, RoutePermissionGuard],
        data: { moduleKey: 'opsRiskCompliance', action: 'View' },
      },

      // Configuration
      {
        path: 'configuration',
        children: [
          {
            path: 'late-fee-configuration',
            component: LateFeeConfigurationComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'configuration.lateFeeConfiguration', action: 'View' },
          },
          {
            path: 'industry-configuration',
            component: IndustryConfigurationComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'configuration.industryConfiguration', action: 'View' },
          },
        ],
      },


      // User Management
      {
        path: 'user-management',
        children: [
          {
            path: '',
            component: UsersAndRequestsComponent,
            canActivate: [AuthGuard, RoutePermissionGuard],
            data: { moduleKey: 'userManagement', action: 'View' },
          },
        ],
      },
    ],
  },

  // Fallback
  { path: '**', redirectTo: '/dashboard' },
];
